import { Form } from "@inertiajs/react"
import { Check } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-32 — Brand-side `Accept application` confirm dialog (UX Spec §2.4).
 *
 * Wraps Inertia `<Form>` (Crew rule) — no shadcn Form, no RHF, no zod.
 * Hidden `event=accept_application` input drives the controller
 * dispatch; the service auto-advances the row to `to_ship` in the same
 * transaction (`accepted` is audit-only on Drop).
 *
 * Default focus on the primary action ("Accept") — per UX Spec §6 #9,
 * the operator wants to confirm; Cancel-by-default would slow the
 * applicant queue.
 */
interface AcceptApplicationConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function AcceptApplicationConfirmDialog({
  row,
  missionId,
}: AcceptApplicationConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            size="sm"
            aria-label={t("collabs.accept_application.aria", {
              title: ariaTitle,
            })}
            data-testid="brand-mission-collab-accept-application-trigger"
          >
            <Check aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.accept_application.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-accept-application-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.accept_application.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.accept_application.body")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="accept_application" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-accept-application-cancel"
                >
                  {t("collabs.accept_application.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-accept-application-confirm"
                >
                  {t("collabs.accept_application.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
